import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useContentFooter, useContentHeader } from "hooks/mainContent";
import SectionTitle from "features/common/components/SectionTitle";
import QuoteStepper from "features/quote/components/QuoteStepper";
import QuoteFooter from "features/quote/components/QuoteFooter";
import { quoteSteps } from "features/quote/constants";
import { useTranslation } from "react-i18next";
import { Grid } from "@material-ui/core";
import CompanyDataContainer from "./CompanyDataContainer";
import CustomDataContainer from "./CustomDataContainer";
import QuestionsContainer from "./QuestionsContainer";
import RequiredDocumentsContainer from "./RequiredDocumentsContainer";
import { useChangeTrackingLens } from "@totalsoft/change-tracking-react";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { emptyArray, emptyObject, emptyString } from "constants/defaultValues";
import { get, set, useStateLens } from "@totalsoft/react-state-lens";
import { isDirty } from "@totalsoft/change-tracking";
import { useApolloClient } from "@apollo/client";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { REQUEST_APPROVAL } from "features/quotes/mutations";
import * as V from "@totalsoft/pure-validations";
import * as R from "@totalsoft/rules-algebra";
import { useValidation } from "@totalsoft/pure-validations-react";
import { useRulesLens } from "@totalsoft/rules-algebra-react";
import { addToast } from "components/notifications";
import { CheckBox, Send } from "@material-ui/icons";
import { quoteStatus } from "constants/quoteStatus";
import { SEND_EMAIL_LINK } from "../mutations";
import { invalidateApolloClientCache } from "utils/functions";
import { QUOTE_QUERY, CHECK_TEG } from "features/dataCompletion/queries";
import useUserData from "hooks/useUserData";
import ProductDocument from "./ProductDocument";

const contactValidator = V.shape({
  firstName: V.required,
  lastName: V.required,
});

const contactRules = R.shape({
  contact:
    R.shape({
      fullName: R.computed((contact) => {
        return contact.firstName + " " + contact.lastName;
      }),
    })
    |> R.when(
      R.propertiesChanged((m) => {
        return [m.contact?.firstName, m.contact?.lastName];
      })
    ),
});

function CompletionData() {
  const { t } = useTranslation();
  const history = useHistory();
  const client = useApolloClient();
  const params = useParams();
  const quoteId = Number(params.quoteId);
  const [, setFooter] = useContentFooter();
  const [, setHeader] = useContentHeader();
  const { showQuestionsInVCP, allowAutomatedDecision, useDownloadProductInStep4 } = useUserData();
  const [disabledByTeg, setDisabledByTeg] = useState(false);

  const [validation, validate] = useValidation(contactValidator);
  const [personStateLens, personStateDirtyInfo, resetPersonStateLens] = useRulesLens(contactRules, {
    offerNotes: emptyString,
    contact: { firstName: "", lastName: "" },
  });

  const [alternativeEMail, setAlternativeEMail] = useState(null);
  const canSendEmail = useMemo(() => {
    const selectedContact = personStateLens.contact |> get;
    if (!selectedContact || selectedContact.webContactInfoId === undefined || selectedContact.webContactInfoId < 0)
      return false;
    if (selectedContact.alternativeEMail != null && selectedContact.alternativeEMail !== emptyString)
      setAlternativeEMail(selectedContact.alternativeEMail);
    else if (selectedContact.email != null && selectedContact.email != emptyString)
      setAlternativeEMail(selectedContact.email);
    return true;
  }, [personStateLens]);

  const [questionsLens, questionsLensDirtyInfo, resetQuestionsLens] = useChangeTrackingLens({
    questions: emptyArray,
  });

  const requiredDocumentsLens = useStateLens(emptyArray);

  const { data, loading } = useQueryWithErrorHandling(QUOTE_QUERY, {
    variables: { quoteId, loadScoringQuestions: !showQuestionsInVCP },
    onCompleted: (data) => {
      if (!data) return;
      if (showQuestionsInVCP) resetQuestionsLens({ questions: data.quote.scoringQuestions });
      (requiredDocumentsLens |> set)(data.quote.requiredDocuments);
    },
  });
  const quote = data?.quote || emptyObject;

  const disabled = useMemo(() => {
    if (!quote?.quoteStatusId) return true;
    return [-1, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13].indexOf(quote.quoteStatusId) > -1;
  }, [quote.quoteStatusId]);

  const uploadedRequiredDocuments = useMemo(() => {
    if ((requiredDocumentsLens |> get).some((item) => item.isMandatory && item.files.length === 0)) return false;
    return true;
  }, [requiredDocumentsLens]);

  const mandatoryQuestionsHaveAnswers = useMemo(() => {
    if (!showQuestionsInVCP || (questionsLens.questions |> get).length === 0) {
      return true;
    }
    if (
      isDirty(questionsLens) ||
      (questionsLens.questions |> get).some(
        (item) =>
          item.isMandatory &&
          (item.questionAnswer === null || (item.variableTypeCode === "S" && item.questionAnswer.length === 0))
      )
    ) {
      return false;
    }
    return true;
  }, [questionsLens, showQuestionsInVCP]);

  const canRequestApproval = useMemo(() => {
    if (
      !loading &&
      uploadedRequiredDocuments &&
      mandatoryQuestionsHaveAnswers &&
      !isDirty(questionsLensDirtyInfo) &&
      (personStateLens.contact.webContactInfoId |> get) > -1 &&
      !isDirty(personStateDirtyInfo) &&
      !disabledByTeg
    )
      return true;
    return false;
  }, [
    uploadedRequiredDocuments,
    mandatoryQuestionsHaveAnswers,
    loading,
    questionsLensDirtyInfo,
    personStateLens.contact.webContactInfoId,
    personStateDirtyInfo,
    disabledByTeg,
  ]);

  const canGoToContract = useMemo(() => {
    if (canRequestApproval || [-4, -5, -6, -7, -8, -9, -10, -11, -12, -13].indexOf(quote.quoteStatusId) > -1) {
      return true;
    }
    return false;
  }, [canRequestApproval, quote.quoteStatusId]);

  const { data: checkTegData } = useQueryWithErrorHandling(CHECK_TEG, {
    skip: !useDownloadProductInStep4,
    variables: { quoteId },
  });

  const [requestApproval] = useMutationWithErrorHandling(REQUEST_APPROVAL);

  const handleComplete = useCallback(async () => {
    if (!validate(personStateLens.contact |> get)) {
      addToast(t("DataCompletion.MissingContactPerson"), "error");
    } else {
      if ([-4, -5, -6, -7, -8, -9, -10, -11, -12, -13].indexOf(quote.quoteStatusId) > -1) {
        history.push(`/quotes/${quoteId}/contract`);
      } else {
        const variables = { quoteId, indicative: false };
        await requestApproval({ variables });
        const fields = ["quoteStatusId", "quoteStatusCode", "quoteStatusName"];
        invalidateApolloClientCache(client, { __typename: "Quote", quoteId }, fields);
        history.push(`/quotes/${quoteId}/contract`);
      }
    }
  }, [t, history, quoteId, quote.quoteStatusId, personStateLens, validate, requestApproval, client]);

  const [sendEmailLink] = useMutationWithErrorHandling(SEND_EMAIL_LINK, {
    onCompleted: () => {
      addToast(t("ToastNotifications.Success"), "success", true);
      if (quote.quoteStatusId === quoteStatus.pendingCustomerInfo.id) return;
      const fields = ["quoteStatusId", "quoteStatusCode", "quoteStatusName"];
      invalidateApolloClientCache(client, { __typename: "Quote", quoteId }, fields);
    },
  });

  const handleSendEmail = useCallback((email) => sendEmailLink({ variables: { quoteId, email: email } }), [
    quoteId,
    sendEmailLink,
  ]);

  const handleBack = useCallback(() => history.push(`/quotes/${quoteId}/products`), [history, quoteId]);

  useEffect(() => {
    if (useDownloadProductInStep4) {
      if (checkTegData?.checkTeg === true) {
        setDisabledByTeg(false);
      } else {
        setDisabledByTeg(true);
      }
    }
  }, [useDownloadProductInStep4, checkTegData]);

  useEffect(() => {
    setHeader(
      <QuoteStepper
        quoteId={quoteId}
        activeStepId={quoteSteps.dataCompletion}
        nextStepId={quoteSteps.contract}
        disabledNextStep={!canGoToContract}
        onHandleNextStep={handleComplete}
      />
    );
  }, [t, setHeader, quoteId, handleComplete, canGoToContract]);

  useEffect(() => {
    setFooter(
      <QuoteFooter
        btnBackLabel={t("General.SelectProduct")}
        btnNextLabel={t("General.RequestApproval")}
        btnBackIcon={<CheckBox />}
        btnNextIcon={<Send />}
        handleBack={handleBack}
        handleComplete={handleComplete}
        btnBackDisabled={false}
        btnNextDisabled={!canRequestApproval}
        quoteId={quoteId}
      />
    );
  }, [t, setFooter, quoteId, canRequestApproval, handleBack, handleComplete]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <SectionTitle title={t("DataCompletion.Title")} subtitle={t("DataCompletion.Subtitle")} />
      </Grid>
      <Grid item xs={12}>
        <CompanyDataContainer />
      </Grid>
      <Grid item xs={12}>
        <CustomDataContainer
          localStateLens={personStateLens}
          localStateDirtyInfo={personStateDirtyInfo}
          resetLocalStateLens={resetPersonStateLens}
          validation={validation}
          validate={validate}
          disabled={disabled}
        />
      </Grid>
      {(showQuestionsInVCP || allowAutomatedDecision) && (
        <Grid item xs={12}>
          <QuestionsContainer
            loading={loading}
            questionsLens={questionsLens}
            questionsLensDirtyInfo={questionsLensDirtyInfo}
            allowAutomatedDecision={allowAutomatedDecision}
            resetQuestionsLens={resetQuestionsLens}
            disabled={disabled}
          />
        </Grid>
      )}
      {useDownloadProductInStep4 && (
        <Grid item xs={12}>
          <ProductDocument quoteId={quoteId} disabledByTeg={disabledByTeg} />
        </Grid>
      )}
      <Grid item xs={12}>
        <RequiredDocumentsContainer
          loading={loading}
          canSendEmail={canSendEmail}
          requiredDocumentsLens={requiredDocumentsLens}
          quoteStatusId={quote.quoteStatusId}
          alternativeEMail={alternativeEMail}
          setAlternativeEMail={setAlternativeEMail}
          onSendEmail={handleSendEmail}
          disabled={disabled}
          disabledByTeg={disabledByTeg}
        />
      </Grid>
    </Grid>
  );
}

export default CompletionData;
